import { Vector2 } from "three";
import { FrameFeatureBox, hasOwnProperty, isNull, PanoFrameFeatureBox } from "../../infra3d";
import { Feature, Geometry, PickData } from "../featuretree/internal";
import { STREAMTYPE } from "../measure/internal";
/**
 * Feature that is only displayed on a specific frame (framenumber,streamid,segmentid)
 * FrameFeature2 extends the Feature class with a frameFeatureBox (a 2d-box-geometry)
 * FrameFeature2 can be serialized to and from geojson.
 *
 * To parse the frameFeatureBox several properties on the geojson-feature are mandatory:
 * nx, ny = normalized (quadtree-)image coordinates of the top left corner of the box
 * w, h = dimensions of the box normalized to the quadtree-dimensions of the image.
 * streamid, framenumber = to identify the GeoFrame to draw the box in.
 * pos = sensor position in the frame
 * projection = either "perspective" or "panoramic"
 *
 * Hint: There are other properties used for loading or styling (e.g. segmentid, category, score, etc.). But
 * they are not mandatory here to draw a FrameFeature.
 *
 *
 * @class FrameFeature2
 * @author Maurus Dähler / Joel Burkhard
 * @extends {Feature}
 * @constructor
 */
export class FrameFeature2 extends Feature {
    constructor(parentcollection) {
        super(parentcollection);
        /**
         * The actual frame feature geometry
         * @property {FrameFeatureBox|PanoFrameFeatureBox} frameFeatureBox
         */
        this.frameFeatureBox = null;
        /**
         * Indicator of the FrameFeature saying if it is currently drawn in the scene.
         * @property {boolean} _isInScene
         */
        this._isInScene = false;
        /**
         * list of mandatory properties a geojson should have to be parsed as frame feature.
         * @property {string[]} _propertycheck
         */
        this._propertycheck = ["nx", "ny", "w", "h", "pos", "projection", "streamid", "framenumber"];
    }
    /**
     * Create a FrameFeature object with content parse from GeoJson Feature node.
     * @method parseFromJSON
     * @param {Object} f a GeoJSON Feature Node.
     */
    parseFromJSON(f) {
        super.parseFromJSON(f);
        this._check();
        if (this.properties["projection"] == "panoramic") {
            const calloteId = parseInt(this.properties.pos);
            this.frameFeatureBox = new PanoFrameFeatureBox(this.properties.nx, this.properties.ny, this.properties.w, this.properties.h, calloteId);
        }
        else {
            this.frameFeatureBox = new FrameFeatureBox(this.properties.nx, this.properties.ny, this.properties.w, this.properties.h);
        }
        this.updateStyle();
        this.update();
    }
    /**
     * Check if the parsed geojson node has the mandatory properties
     * @method _check
     */
    _check() {
        for (let i = 0; i < this._propertycheck.length; i++) {
            if (!hasOwnProperty(this.properties, this._propertycheck[i]))
                throw new Error("geojson could not be parsed as FrameFeature. Missing property " + this._propertycheck[i]);
        }
    }
    /**
     * redraws the geoemtry and updates according to the current frame
     * @method update
     * @param {boolean} needsrender
     */
    update(needsrender = false) {
        super.update(needsrender);
        if (this.parentcollection.parentfeaturehandler.parentscene.getCurrentGeoframe()) {
            this.updateframe(this.parentcollection.parentfeaturehandler.parentscene.getCurrentGeoframe());
        }
    }
    /**
     * Adds/Removes the FrameFeature to the scene according to the current GeoFrame.
     * @method updateframe
     * @param geoframe
     */
    updateframe(geoframe) {
        if (this.frameFeatureBox != null) {
            const currentFrameNum = geoframe.getFramenumber();
            const currentStreamId = geoframe.stream;
            const isleftright = () => {
                if (geoframe.getMeasureInfo().streamtype === STREAMTYPE.STEREO) {
                    const currentActivimage = geoframe.parentGeoFrameCollection.activeimage;
                    if (currentActivimage) {
                        if (currentActivimage == "left" && this.properties.pos == 0)
                            return true;
                        if (currentActivimage == "right" && this.properties.pos == 1)
                            return true;
                        return false;
                    }
                }
                return true;
            };
            if (this.properties.framenumber == currentFrameNum &&
                this.properties.streamid == currentStreamId &&
                isleftright()) {
                this.scene.infrascene.addForFrame(this.frameFeatureBox);
                this._isInScene = true;
            }
            else {
                if (this._isInScene)
                    this.scene.infrascene.removeForFrame(this.frameFeatureBox);
                this._isInScene = false;
            }
        }
    }
    show() {
        super.show();
        this.frameFeatureBox.show();
    }
    hide() {
        super.hide();
        this.frameFeatureBox.hide();
    }
    /**
     * highlights the geometry
     * @method highlightOn
     * @param {boolean?} needsrender
     */
    highlightOn(needsrender = false) {
        if (this.frameFeatureBox != null && !this.highlighton) {
            this.frameFeatureBox.highlightOn();
            if (needsrender)
                this.parentcollection.infrascene.render();
            this.highlighton = true;
        }
    }
    /**
     * unhighligt the geometry
     * @method highlightOff
     * @param {boolean?} needsrender
     */
    highlightOff(needsrender = false) {
        if (this.frameFeatureBox != null && this.highlighton) {
            this.frameFeatureBox.highlightOff();
            if (needsrender)
                this.parentcollection.infrascene.render();
            this.highlighton = false;
        }
    }
    /**
     * picks frame features in the scene using mouse coordinates
     *
     * @param mx mouse x-coordinate
     * @param my mouse y-coordinate
     * @param {PickData[]} pickedfeatures Array of pick-results
     */
    pick(mx, my, pickedfeatures) {
        if (!isNull(this.frameFeatureBox) && this._isInScene) {
            const picked = this.frameFeatureBox.pick(mx, my);
            if (picked.length > 0) {
                const pdata = new PickData();
                pdata.feature = this;
                pdata.mindist = 0.0;
                pdata.mx = mx;
                pdata.my = my;
                pdata.found = true;
                pdata.disttopoint = 0.0;
                pdata.found_mx = mx;
                pdata.found_my = mx;
                pdata.geometry = new Geometry(this);
                pickedfeatures.push(pdata);
            }
        }
    }
    /**
     * Changes the style of the feature
     * @param {FeatureStyle} style
     * @param {FeatureStyle} highlightstyle
     */
    changeStyle(style, highlightstyle) {
        this.style = style;
        this.highlightstyle = highlightstyle;
        this.updateStyle();
    }
    /**
     * updates the style of the geometry (frame feature box) according to its properties.
     */
    updateStyle() {
        const matdef = this.style.toMaterialDefinitions(this.properties);
        this.frameFeatureBox.updateStyle(matdef);
    }
    /**
     * @description Recalculates the corners and size of the FrameFeatureBox
     * @param p1 movepoint, opposite point that should be current mouse coordinates
     * @param p2 anchorpoint, this should always be the same (first click)
     */
    resize(p1, p2) {
        const mapcoordsSmallest = new Vector2();
        const mapcoordsBiggest = new Vector2();
        if (p1.x < p2.x) {
            mapcoordsSmallest.x = p1.x;
            mapcoordsBiggest.x = p2.x;
        }
        else {
            mapcoordsSmallest.x = p2.x;
            mapcoordsBiggest.x = p1.x;
        }
        if (p1.y < p2.y) {
            mapcoordsSmallest.y = p1.y;
            mapcoordsBiggest.y = p2.y;
        }
        else {
            mapcoordsSmallest.y = p2.y;
            mapcoordsBiggest.y = p1.y;
        }
        const w = mapcoordsBiggest.x - mapcoordsSmallest.x;
        const h = mapcoordsBiggest.y - mapcoordsSmallest.y;
        this.properties.w = w;
        this.properties.h = h;
        this.properties.nx = mapcoordsSmallest.x;
        this.properties.ny = mapcoordsSmallest.y;
        this.frameFeatureBox.resize(this.properties.nx, this.properties.ny, this.properties.w, this.properties.h);
    }
    /**
     *  Destroys the feature
     *  @method destroy
     *  @param {boolean?} needsrender
     */
    destroy(needsrender = false) {
        super.destroy(needsrender);
        if (this.frameFeatureBox) {
            if (this._isInScene)
                this.scene.infrascene.remove(this.frameFeatureBox);
        }
        this.frameFeatureBox = null;
    }
}
//# sourceMappingURL=framefeature2.js.map